<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { ArrowRightDownIcon, ArrowRightUpIcon, MinusIcon } from '@solar-icons/vue/linear'
import { useFormatters } from '../../../composables/useFormatters'
import type { DeltaValue } from './app-delta-pill-types'

const props = defineProps<{
  delta: DeltaValue
  /** What the change is measured against, e.g. "vs prior 4 weeks". Omit where the caption repeats. */
  caption?: string
  /** Drops the good/bad colour and keeps only the arrow, for a change that is somebody else's. */
  muted?: boolean
}>()

const { t } = useI18n()
const { formatDecimal } = useFormatters()

const TONE: Record<DeltaValue['direction'], string> = {
  up: 'text-accent-green',
  down: 'text-accent-red',
  flat: 'text-text-tertiary',
}

const ICON = {
  up: ArrowRightUpIcon,
  down: ArrowRightDownIcon,
  flat: MinusIcon,
}

const UNIT_KEY: Record<DeltaValue['unit'], string> = {
  percent: 'primitives.delta.percent',
  points: 'primitives.delta.points',
  absolute: 'primitives.delta.absolute',
}

const magnitude = computed(() =>
  t(UNIT_KEY[props.delta.unit], { value: formatDecimal(props.delta.magnitude) }),
)

const label = computed(() =>
  props.delta.direction === 'flat' ? t('primitives.delta.unchanged') : magnitude.value,
)

const toneClass = computed(() =>
  props.muted ? 'text-text-secondary' : TONE[props.delta.direction],
)
</script>

<template>
  <span class="inline-flex items-center gap-1 whitespace-nowrap text-caption tabular-nums">
    <component :is="ICON[delta.direction]" class="shrink-0" :class="toneClass" />
    <span :class="toneClass">{{ label }}</span>
    <span v-if="caption" class="text-text-tertiary">{{ caption }}</span>
  </span>
</template>
